import React, { type FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { SVGIcon } from 'ic-snacks'
import {
  borderRadius,
  colors,
  Checkbox,
  defaultLineHeight,
  fontSize,
  spacing,
} from '@retailer-platform/shared-components'
import { DateRangePicker, SingleDatePicker } from 'react-dates'
import moment, { type Moment } from 'moment'
import { dateFormat } from 'domains/merchandising/constants/heroBanner.constants'
import { MerchandisingFormInputWrapper } from 'domains/merchandising/components/form-input/MerchandisingFormInputWrapper'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import { useQueryLiveHeroBanner } from 'domains/merchandising/utils/useQueryLiveHeroBanner.hooks'
import { getTimezoneOptions } from 'domains/merchandising/utils/heroBannerUtils'
import { useDashMessages, useDashIntl } from 'intl/intl.hooks'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'

type DateType = Moment | null

type StartEndDate = {
  startDate: DateType
  endDate: DateType
}

interface Props extends Omit<ControllerProps, 'name'> {}

const wrapperStyles = css({
  alignItems: 'center',
  display: 'flex',

  // Overwrite some of the Date picker styles to fit with IDS
  '.DateRangePickerInput, .SingleDatePicker .DateInput_input': {
    borderRadius: borderRadius.X8,
    color: colors.GRAYSCALE.X50,
    fontSize: '13px', // use the same font size for range/single picker
    lineHeight: '13px',
    height: '46px',
  },

  '.DateRangePicker .DateInput_input__small': {
    color: colors.GRAYSCALE.X70,
  },
})

const checkboxWrapper = css({
  alignItems: 'center',
  display: 'flex',
  fontSize: fontSize.X12,
  marginLeft: spacing.X8,
  marginTop: '20px', // Offset the label
})

const checkboxLabelStyles = css({
  cursor: 'pointer',
})

const footnoteStyles = css({
  color: colors.GRAYSCALE.X70,
  display: 'flex',
  fontSize: fontSize.X12,
  lineHeight: defaultLineHeight,
  maxWidth: '645px',
  marginTop: spacing.X16,
  width: '100%',

  '& > svg': {
    marginRight: spacing.X4,
  },
})

export const HeroBannerFormDetailScheduler: FunctionComponent<Props> = ({ form }) => {
  const { register, getValues, setValue } = form
  const originalStartDate = getValues('scheduling.startDate')
  const originalEndDate = getValues('scheduling.endDate')

  // Set dates if a response is returned from API, if not start date is today, and no end date is set
  // State is used for visual representation in the input field only.
  const [dates, setDates] = useState({
    startDate: originalStartDate ? moment(originalStartDate) : moment(),
    endDate: originalEndDate ? moment(originalEndDate) : null,
  })

  const [showEndDate, setShowEndDate] = useState(!!getValues('scheduling.endDate'))
  // Focused state for date range
  const [focusedInput, setFocusedInput] = useState(null)
  // Focused state for single date picker
  const [focused, setFocused] = useState(null)

  // Handle date picker range change (start/end date)
  const onDateRangeChange = (newDates: StartEndDate) => {
    // Set state for visual form field
    setDates(newDates)

    // Set value for react hook form
    setValue('scheduling.startDate', newDates.startDate?.utc())
    setValue('scheduling.endDate', newDates.endDate?.utc())
  }

  // Handle single date picker change (start date)
  const onDateChange = (newDate: DateType) => {
    // Set state for visual form field
    setDates({ startDate: newDate, endDate: null })

    // Set value for react hook form
    setValue('scheduling.startDate', newDate?.utc())
    setValue('scheduling.endDate', null)
  }

  const onSetNoEndDate = () => {
    setShowEndDate(!showEndDate)
  }

  useEffect(() => {
    if (showEndDate) {
      setValue('scheduling.endDate', dates.endDate)
    } else {
      setValue('scheduling.endDate', null)
    }
  }, [showEndDate, dates, setValue])

  const messages = useDashMessages({
    datesLabel: 'merchandising.hero-banners.section.detail.field.dates.label',
    checkboxDescription: 'merchandising.hero-banners.section.detail.field.dates.checkbox',
    defaultEndDate: 'merchandising.hero-banners.field.empty.value',
  })

  const intl = useDashIntl()

  const { banners: existingLiveBanner } = useQueryLiveHeroBanner()
  let liveBannerWarning = null

  if (existingLiveBanner.length > 0) {
    const liveBanner = existingLiveBanner[0]
    // Dates need to be converted back to readable format (not moment)
    const afterLiveStartDate =
      liveBanner.scheduling.startDate >= dates.startDate?.format(dateFormat)
    // If the end date is not defined, return true
    const beforeLiveEndDate =
      !liveBanner.scheduling.endDate || !dates.endDate
        ? true
        : liveBanner.scheduling.endDate < dates.endDate?.format(dateFormat)

    // if the current start date falls between the range of the live banner,
    // we should show a warning
    if (afterLiveStartDate && beforeLiveEndDate) {
      const formattedLiveStartDate = moment(liveBanner.scheduling.startDate).format(dateFormat)
      const formattedLiveEndDate = liveBanner.scheduling.endDate
        ? moment(liveBanner.scheduling.startDate).format(dateFormat)
        : messages.defaultEndDate

      liveBannerWarning = intl.formatMessage(
        { id: 'merchandising.hero-banners.section.detail.liveWarning' },
        {
          name: liveBanner.name,
          dates: `${formattedLiveStartDate} - ${formattedLiveEndDate}`,
        }
      )
    }
  }

  return (
    <>
      <MerchandisingFormRow>
        <div css={wrapperStyles}>
          <MerchandisingFormInputWrapper label={messages.datesLabel}>
            {showEndDate ? (
              <DateRangePicker
                small
                withPortal
                minimumNights={0}
                startDateId="startDate"
                endDateId="endDate"
                startDate={dates.startDate}
                endDate={dates.endDate}
                focusedInput={focusedInput}
                onFocusChange={focusedInput => setFocusedInput(focusedInput)}
                onDatesChange={newDates => onDateRangeChange(newDates)}
              />
            ) : (
              <SingleDatePicker
                id="merchandisingSchedulerSingleDate"
                small
                withPortal
                date={dates.startDate}
                focused={focused}
                onFocusChange={({ focused }) => setFocused(focused)}
                onDateChange={newDates => onDateChange(newDates)}
              />
            )}
          </MerchandisingFormInputWrapper>
          <div css={checkboxWrapper}>
            <Checkbox
              id="noEndDateCheckbox"
              name="noEndDate"
              checked={!showEndDate}
              onChange={() => onSetNoEndDate()}
            />
            <label css={checkboxLabelStyles} htmlFor="noEndDateCheckbox">
              {messages.checkboxDescription}
            </label>
          </div>
        </div>
      </MerchandisingFormRow>
      <MerchandisingFormField>
        <MerchandisingFormSelect
          form={form}
          identifier="scheduling.timezone"
          options={getTimezoneOptions()}
          labelId={'merchandising.hero-banners.section.detail.field.timezone.label'}
        />
      </MerchandisingFormField>
      {liveBannerWarning && (
        <div css={footnoteStyles}>
          <SVGIcon name="infoFilled" size="small" />
          {liveBannerWarning}
        </div>
      )}
      <input name="scheduling.startDate" ref={register} type="hidden" />
      <input name="scheduling.endDate" ref={register} type="hidden" />
    </>
  )
}
